import styled from "styled-components";
import { ButtonAppearance, ButtonStateAppearance } from "./ButtonAppearances";
import * as Color from "../Color";

/**
 * Converts a ButtonStateAppearance to its CSS string representation.
 * @param stateAppearance The button state appearance to convert.
 * @returns The converted text.
 */
function buttonStateAppearanceToCss(stateAppearance: ButtonStateAppearance): string {
  const { foregroundColor, backgroundColor, borderColor, textUnderline } = stateAppearance;

  return `
    color: ${Color.toString(foregroundColor)};
    background-color: ${Color.toString(backgroundColor)};
    border-color: ${Color.toString(borderColor ?? backgroundColor)};
    text-decoration: ${textUnderline ? "underline" : "none"};

    .icon {
      path,
      circle,
      g {
        fill: ${Color.toString(foregroundColor)};
      }
    }
  `;
}

/**
 * The button with the appearance styling applied.
 */
export const StyledButton = styled.button<{ appearance: ButtonAppearance; iconOnly: boolean }>`
  display: inline-block;
  max-height: content-height;
  text-transform: ${(props) => (props.appearance.label.uppercase ? "uppercase" : "capitalize")};
  cursor: pointer;
  letter-spacing: ${(props) => props.appearance.label.letterSpacing}px;
  white-space: nowrap;
  padding: ${(props) => {
    const { vertical } = props.appearance.padding;
    let { horizontal } = props.appearance.padding;
    if (props.iconOnly) horizontal = vertical;

    return `${vertical / 16}rem ${horizontal / 16}rem`;
  }};
  font-size: ${(props) => props.appearance.label.fontSize / 16}rem;
  line-height: ${(props) => props.appearance.label.lineHeight / 16}rem;
  border-radius: 0.25rem;
  border-width: 1px;
  border-style: solid;
  transition: color ease 0.2s, background-color ease 0.2s, border-color ease 0.2s;
  text-decoration: ${(props) => (props.appearance.normal.textUnderline ? "underline" : "none")};
  ${(props) => buttonStateAppearanceToCss(props.appearance.normal)}

  .icon {
    path,
    circle,
    g {
      fill: ${(props) => Color.toString(props.appearance.normal.foregroundColor)};
    }
  }

  &:hover {
    ${(props) => buttonStateAppearanceToCss(props.appearance.hover)}
  }

  &:active,
  &.down {
    ${(props) => buttonStateAppearanceToCss(props.appearance.pressed)}
  }

  &:disabled,
  &.disabled {
    cursor: default;
    ${(props) => buttonStateAppearanceToCss(props.appearance.disabled)}
  }
`;
